'use client';

import { useEffect, useRef } from 'react';
import { useCallbackRef, usePersonalizationTracker } from '@skin-seoul/utils';
import { createAnalyticsEventId } from '../../infrastructure/analytics/analyticsAPIClient';
import { normalizeAnalyticsIdentifier } from './analyticsIdentity';

interface AnalyticsPageVisitOptions {
  pathname: string;
  onView: (uniqueId: string) => void;
  enabled?: boolean;
  canTrack?: () => boolean;
}

/** Tracks only the active visit; actor changes never replay a page that was left. */
export function useAnalyticsPageVisit({ pathname, onView, enabled = true, canTrack }: AnalyticsPageVisitOptions) {
  const tracker = usePersonalizationTracker();
  const visit = useRef({ pathname, sent: false });
  const send = useCallbackRef(onView);
  const isAllowed = useCallbackRef(() => canTrack?.() ?? true);

  useEffect(() => {
    if (visit.current.pathname !== pathname) {
      visit.current = { pathname, sent: false };
    }
    let active = true;
    const emit = () => {
      if (
        !active ||
        !enabled ||
        visit.current.sent ||
        window.location.pathname !== pathname ||
        !normalizeAnalyticsIdentifier(tracker.getActorId()) ||
        !isAllowed()
      ) {
        return;
      }
      visit.current.sent = true;
      send(createAnalyticsEventId());
    };
    const handlePageHide = () => {
      active = false;
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      active = true;
      visit.current = { pathname, sent: false };
      emit();
    };

    // Subscribe before reading so an actor resolved during setup is not missed.
    const unsubscribe = tracker.onActorIdChange(emit);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    emit();
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [enabled, isAllowed, pathname, send, tracker]);
}
